import { useEffect, useState, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";

/**
 * PWA Update Notification Component
 * 
 * Detects when a new version of the PWA is available and prompts the user to update.
 * Monitors service worker updates and shows notifications when updates are available.
 */
const PWAUpdateNotification = () => {
  const { toast } = useToast();
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [swRegistration, setSwRegistration] = useState<ServiceWorkerRegistration | null>(null);

  const showUpdateNotification = useCallback(() => {
    toast({
      title: "New version available",
      description: "A new version of the app is available. Would you like to update now?",
      action: (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setUpdateAvailable(false);
              toast({
                title: "Update dismissed",
                description: "You can update later by refreshing the page.",
              });
            }}
          >
            Later
          </Button>
          <Button
            size="sm"
            onClick={() => {
              if (swRegistration && swRegistration.waiting) {
                // Tell the waiting service worker to skip waiting and activate
                swRegistration.waiting.postMessage({ type: "SKIP_WAITING" });
                toast({
                  title: "Updating...",
                  description: "The app is being updated. Please wait.",
                });
                // Reload after a short delay to allow the service worker to activate
                setTimeout(() => {
                  window.location.reload();
                }, 500);
              } else {
                // Fallback: just reload the page
                window.location.reload();
              }
            }}
          >
            Update Now
          </Button>
        </div>
      ),
    });
  }, [toast, swRegistration]);

  useEffect(() => {
    // Check if service workers are supported
    if (!("serviceWorker" in navigator)) {
      return;
    }

    // Monitor service worker updates
    const handleServiceWorkerUpdate = () => {
      if (swRegistration) {
        swRegistration.addEventListener("updatefound", () => {
          const newWorker = swRegistration.installing;
          if (newWorker) {
            newWorker.addEventListener("statechange", () => {
              if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
                // New service worker is installed and waiting
                setUpdateAvailable(true);
                showUpdateNotification();
              }
            });
          }
        });
      }
    };

    // Get service worker registration
    navigator.serviceWorker.ready.then((registration) => {
      setSwRegistration(registration);
      handleServiceWorkerUpdate();
    });

    // Listen for controller change (when new service worker takes over)
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      window.location.reload();
    });
  }, [swRegistration, showUpdateNotification]);

  // This component doesn't render anything visible
  // It uses toast notifications to inform users about updates
  return null;
};

export default PWAUpdateNotification;

